import { useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getBriefRelatedInsight, getInsightPeersPath, getProjectSearchAiBrief } from "pages/ProjectDashboard";

// Store
import { showLoader } from "store/slice/UI";
import { addBriefsData, fetchProjectBrief, getBriefs, updateBriefChild } from "store/slice/Project/briefs/briefsSlice";
import { addInsightsData, getInsights, getProjectInsight } from "store/slice/Project/insights/insightsSlice";
import { addSearchAisData, updateSearchAiChild } from "store/slice/Project/searchAis/searchAisSlice";

// Utils
import { isEmpty } from "lodash";

// Services
import { saveProjectByPathService, updateProjectByPathService, createSearchAIService } from "services";
import { BriefChildren, InsightData, ProjectSearchAICreationResponse } from "services/projects/project.types";
import { TModalFor } from "./CreateProjectSectionModal";

const useCreateProjectSectionModal = (
    projectId: string | undefined | null,
    parentSectionId: string | number | null,
    onCreateProjectSectionCallback?: (createdSectionData: any) => void
): {
    showModalFor: TModalFor;
    newSectioName: string;
    isModalSaving: boolean;
    handleShowCreateProjectSectionModal: (section: TModalFor, initialName?: string) => void;
    handleCreateProjectSection: () => Promise<void>;
    handleUpdateSectionName: (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleCloseProjectSectionCreateModal: () => void;
} => {
    const dispatch = useAppDispatch();

    const { data: insightsList } = useAppSelector(getInsights);
    const { data: briefsList } = useAppSelector(getBriefs);

    const [newSectioName, setNewSectionName] = useState("");
    // const [isNameInvalid, setIsNameInvalid] = useState(true);
    const [isModalSaving, setIsModalSaving] = useState(false);
    const [showModalFor, setShowModalFor] = useState<TModalFor>("");

    const handleShowCreateProjectSectionModal = (section: TModalFor, initialName?: string) => {
        setShowModalFor(section);
        if (initialName) {
            setNewSectionName(initialName);
        }
    };

    const handleUpdateSectionName = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setNewSectionName(ev.target.value);
    };

    const handleCloseProjectSectionCreateModal = () => {
        setShowModalFor("");
        setNewSectionName("");
        // setIsNameInvalid(false);
    };

    const handleCreateProjectSection = async () => {
        if (!projectId) return;

        dispatch(showLoader(true));
        setIsModalSaving(true);

        let createdSectionData = {} as InsightData | BriefChildren | ProjectSearchAICreationResponse;

        if (showModalFor === "LD Dashboard") {
            try {
                const { data } = await saveProjectByPathService({
                    id: Number(projectId),
                    project_path: `project_${projectId}/insight_list`,
                    data: { name: newSectioName },
                    echo: true,
                });

                setNewSectionName("");
                createdSectionData = data.children.at(-1) as InsightData;
                dispatch(addInsightsData([createdSectionData]));
            } catch (error) {
                console.error(error);
                dispatch(showLoader(false));
            }
        }

        if (showModalFor === "Brief" && parentSectionId) {
            // Create Brief
            try {
                const { data } = await saveProjectByPathService({
                    id: Number(projectId),
                    project_path: `project_${projectId}/brief_list`,
                    data: { name: newSectioName, insight_id: Number(parentSectionId) },
                    echo: true,
                });

                createdSectionData = data.children.at(-1) as BriefChildren;
                dispatch(addBriefsData([createdSectionData]));
            } catch (error) {
                console.error(error);
                dispatch(showLoader(false));
            }

            // Update SearchAI reference path on Brief
            if (!isEmpty(createdSectionData)) {
                try {
                    const briefId = createdSectionData.data.id;
                    const payload = {
                        data: {},
                        ref: {
                            path: `/project_${projectId}/insight_list/insight_${parentSectionId}`,
                        },
                    };

                    await updateProjectByPathService({
                        id: Number(projectId),
                        project_path: `project_${projectId}/brief_list/brief_${briefId}/insight_ref`,
                        data: payload,
                    });

                    const briefInsightfRef = getBriefRelatedInsight(createdSectionData as BriefChildren);

                    if (briefInsightfRef && briefId) {
                        const updatedData = {
                            ...briefInsightfRef,
                            ref: payload.ref,
                        };

                        dispatch(
                            updateBriefChild({
                                data: updatedData,
                                id: Number(briefId),
                                type: "PROJECT_INSIGHT_REF",
                            })
                        );
                    }
                } catch (error) {
                    console.error(error);
                    dispatch(showLoader(false));
                }
            }
        }

        if (showModalFor === "SearchAi" && parentSectionId) {
            let briefData = briefsList.find((brief) => brief.data.id === Number(parentSectionId));
            let insightData: InsightData | undefined = undefined;

            if (!briefData) {
                try {
                    briefData = unwrapResult(
                        await dispatch(
                            fetchProjectBrief({
                                projectId: Number(projectId),
                                path: `project_${projectId}/brief_list/brief_${parentSectionId}`,
                            })
                        )
                    );
                } catch (error) {
                    console.error(error);
                    dispatch(showLoader(false));
                }
            }

            const insightRef = getBriefRelatedInsight(briefData)?.ref.path;
            insightData = insightsList.find((insight) => insight.meta.path === insightRef);

            if (!insightData && insightRef) {
                try {
                    insightData = unwrapResult(
                        await dispatch(
                            getProjectInsight({
                                projectId,
                                path: insightRef,
                            })
                        )
                    );
                } catch (error) {
                    console.error(error);
                    dispatch(showLoader(false));
                }
            }

            const companySetPath = getInsightPeersPath(insightData)?.ref?.path;

            if (!companySetPath) {
                dispatch(showLoader(false));
                return;
            }

            // Create Search AI
            try {
                const { data } = await createSearchAIService({
                    id: Number(projectId),
                    data: {
                        search_ai_name: newSectioName,
                        peers_company_set_path: companySetPath,
                        company_set_copy_name: `Copy Peers ` + `${new Date().getTime()}`.slice(-5),
                    },
                });
                dispatch(addSearchAisData([data]));
                createdSectionData = data;
            } catch (error) {
                dispatch(showLoader(false));
                console.error(error);
                return;
            }

            // Update SearchAI reference path on Brief
            if (!isEmpty(createdSectionData)) {
                try {
                    const searchAiId = createdSectionData.data.id;
                    const payload = {
                        data: {},
                        ref: {
                            path: `/project_${projectId}/brief_list/brief_${parentSectionId}`,
                        },
                    };

                    await updateProjectByPathService({
                        id: Number(projectId),
                        project_path: `project_${projectId}/search_ai_list/search_ai_${searchAiId}/brief_ref`,
                        data: payload,
                    });

                    const searchAiBriefRef = getProjectSearchAiBrief(createdSectionData);

                    if (searchAiBriefRef && searchAiId) {
                        const updatedData = {
                            ...searchAiBriefRef,
                            ref: payload.ref,
                        };

                        dispatch(
                            updateSearchAiChild({
                                data: updatedData,
                                id: Number(searchAiId),
                                type: "PROJECT_BRIEF_REF",
                            })
                        );
                    }
                } catch (error) {
                    dispatch(showLoader(false));
                    console.error(error);
                    return;
                }
            }
        }

        setIsModalSaving(false);
        setShowModalFor("");
        setNewSectionName("");
        // setIsNameInvalid(false);

        if (onCreateProjectSectionCallback) {
            onCreateProjectSectionCallback(createdSectionData);
        } else {
            dispatch(showLoader(false));
        }
    };

    return {
        showModalFor,
        newSectioName,
        isModalSaving,
        handleShowCreateProjectSectionModal,
        handleCreateProjectSection,
        handleUpdateSectionName,
        handleCloseProjectSectionCreateModal,
    };
};

export default useCreateProjectSectionModal;
